import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import accessApi from '../services/accessApi';
import { onUnauthorized } from '../services/authEvents';
import { themeManager } from '../utils/themeManager';
import SinatraLogo from './SinatraLogo';
import Icon from './Icon';
import FallingTiles from './FallingTiles';
// Shared buttons and inputs (rm-button, rm-input)
import './RelationshipManagers.css';
import './AuthGate.css';

const AuthContext = createContext({ user: null, isAdmin: false, signOut: () => {} });

// The signed-in person: { user: { id, email, name, picture_url, roles }, isAdmin, signOut }
export const useAuth = () => useContext(AuthContext);

const GOOGLE_SCRIPT = 'https://accounts.google.com/gsi/client';
let googleScript = null;

// Google Identity Services, loaded once and only on the sign-in page
const loadGoogle = () => {
  if (!googleScript) {
    googleScript = new Promise((resolve, reject) => {
      if (window.google?.accounts?.id) {
        resolve(window.google);
        return;
      }
      const script = document.createElement('script');
      script.src = GOOGLE_SCRIPT;
      script.async = true;
      script.onload = () => resolve(window.google);
      script.onerror = () => {
        googleScript = null;
        reject(new Error("Couldn't reach Google sign-in; check the connection and reload"));
      };
      document.head.appendChild(script);
    });
  }
  return googleScript;
};

const SignInPage = ({ config, busy, message, onGoogle, onDevLogin }) => {
  const buttonRef = useRef(null);
  const [googleError, setGoogleError] = useState(null);
  const [email, setEmail] = useState('');
  const clientId = config?.google_client_id;

  useEffect(() => {
    if (!clientId) return undefined;
    let cancelled = false;
    loadGoogle()
      .then(google => {
        if (cancelled || !buttonRef.current) return;
        google.accounts.id.initialize({ client_id: clientId, callback: (response) => onGoogle(response.credential) });
        google.accounts.id.renderButton(buttonRef.current, {
          theme: document.documentElement.getAttribute('data-theme') === 'dark' ? 'filled_black' : 'outline',
          size: 'large',
          text: 'signin_with',
          shape: 'pill',
          width: 280
        });
      })
      .catch(err => {
        if (!cancelled) setGoogleError(err.message);
      });
    return () => {
      cancelled = true;
    };
  }, [clientId, onGoogle]);

  return (
    <main className="auth-page">
      <FallingTiles />
      <section className="auth-card" aria-labelledby="auth-title">
        <div className="auth-brand">
          <SinatraLogo markSize={38} />
        </div>
        <h1 id="auth-title">Sign in</h1>
        <p className="auth-lead">Use your Google account. An admin gives each person access.</p>

        {clientId && <div className="auth-google" ref={buttonRef} />}
        {config && !clientId && (
          <p className="auth-note">Google sign-in isn't set up yet: the server has no GOOGLE_CLIENT_ID.</p>
        )}
        {busy && <p className="auth-status" role="status"><Icon name="loader" spin /> Signing in...</p>}
        {(message || googleError) && (
          <p className="auth-error" role="alert"><Icon name="alert-triangle" /> {message || googleError}</p>
        )}

        {config?.dev_login && (
          <form
            className="auth-dev"
            onSubmit={(event) => {
              event.preventDefault();
              if (email.trim()) onDevLogin(email.trim());
            }}
          >
            <p className="auth-dev__title"><Icon name="alert-triangle" /> Development sign-in</p>
            <p className="auth-dev__hint">
              On because the server sets AUTH_DEV_LOGIN. Turn it off before anyone else can reach this app.
            </p>
            <label htmlFor="auth-dev-email">Email of an existing user</label>
            <div className="auth-dev__row">
              <input
                id="auth-dev-email"
                type="email"
                className="rm-input"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                autoComplete="off"
              />
              <button type="submit" className="rm-button" disabled={busy || !email.trim()}>Sign in</button>
            </div>
          </form>
        )}
      </section>
    </main>
  );
};

/**
 * Shows the app to someone signed in with a role, and the sign-in page to everyone else,
 * including after the session ends (any API call answering 401).
 */
const AuthGate = ({ children }) => {
  // undefined while checking, null when nobody is signed in
  const [user, setUser] = useState(undefined);
  const [config, setConfig] = useState(null);
  const [message, setMessage] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    themeManager.initializeTheme();
  }, []);

  useEffect(() => onUnauthorized(() => setUser(null)), []);

  useEffect(() => {
    let cancelled = false;
    accessApi.me()
      .then(me => {
        if (!cancelled) setUser(me);
      })
      .catch(() => {
        if (!cancelled) setUser(null);
      });
    accessApi.config()
      .then(settings => {
        if (!cancelled) setConfig(settings);
      })
      .catch(() => {
        if (!cancelled) setConfig({ google_client_id: null, dev_login: false });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const finishSignIn = useCallback(async (attempt) => {
    setBusy(true);
    setMessage(null);
    try {
      setUser(await attempt());
    } catch (err) {
      setMessage(err.message);
    } finally {
      setBusy(false);
    }
  }, []);

  const handleGoogle = useCallback(
    (credential) => finishSignIn(() => accessApi.googleSignIn(credential)), [finishSignIn]);
  const handleDevLogin = useCallback(
    (email) => finishSignIn(() => accessApi.devLogin(email)), [finishSignIn]);

  const signOut = useCallback(async () => {
    try {
      await accessApi.signOut();
    } finally {
      setMessage(null);
      setUser(null);
    }
  }, []);

  if (user === undefined) {
    return (
      <main className="auth-page">
        <p className="auth-status" role="status"><Icon name="loader" spin /> Loading...</p>
      </main>
    );
  }
  if (!user) {
    return (
      <SignInPage config={config} busy={busy} message={message} onGoogle={handleGoogle} onDevLogin={handleDevLogin} />
    );
  }
  return (
    <AuthContext.Provider value={{ user, isAdmin: user.roles.includes('admin'), signOut }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthGate;
